import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAdministrator } from '../../client/lib/platformRole';
import {
  ChevronRight,
  ChevronDown,
  Circle,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { LayoutConfig } from './types';
import logo from '../../client/static/App-ca.png';
import { getSectionVisibility, getOrganizationBySlug, getLocations } from 'wasp/client/operations';
import { useQuery } from 'wasp/client/operations';
import {
  HUB_LANDING_PATH,
  isHubLandingPath,
  isHubLocationSlug,
  sortHubLocations,
} from '../../shared/hubLocations';

// Routes that should use the clean Community layout are prefixed with /p/

interface CommonSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  config: LayoutConfig;
  onNavigate?: () => void;
  user?: { platformRole?: string } | null;
}

function CommonSidebar({ isOpen, onToggle, config, onNavigate, user }: CommonSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCompact, setIsCompact] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isHubLanding = isHubLandingPath(location.pathname);

  function goHome() {
    navigate(HUB_LANDING_PATH);
  }
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    config.menuSections.reduce((acc, section) => {
      acc[section.label] = section.defaultOpen ?? true;
      return acc;
    }, {} as Record<string, boolean>)
  );
  const [visibleSections, setVisibleSections] = useState<Record<string, { isVisible: boolean, isActive: boolean, customLabel?: string | null }>>({});
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);
  // Persists the last active org and location slugs so sticky paths keep context
  const [pinnedOrgSlug, setPinnedOrgSlug] = useState<string | undefined>(undefined);
  const [pinnedLocationSlug, setPinnedLocationSlug] = useState<string | undefined>(undefined);

  // Detect org page context: /org/:slug or /org/:slug/:org-section (routing uses slug directly)
  const ORG_SECTIONS = new Set(['about', 'notices', 'events', 'tenants', 'food-court', 'facilities', 'services', 'buy-sell', 'request-offer', 'lost-found', 'transportation', 'information-desk']);
  const SYSTEM_PATH_PREFIXES = new Set(['admin', 'login', 'signup', 'account', 'notifications', 'dashboard', 'reception', 'pricing', 'checkout', 'search', 'occasions', 'topics', 'discover-services', 'meet-the-team', 'organization-types', 'organizations', 'about', 'privacy', 'terms', 'roadmap', 'contact', 'request-password-reset', 'password-reset', 'email-verification', 'pending-approval', 'file-upload', 'tools', 'test-dashboard', 'select-location', 'verify-email', 'locations', 'events', 'tenants', 'notices', 'facilities', 'buy-sell', 'food-court', 'request-offer', 'transportation', 'lost-found', 'services']);
  const STICKY_ORG_PATHS = new Set<string>();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0] ?? '';
  const secondSegment = pathSegments[1] ?? '';

  // 1. Check if it's an Org page (prefix /org/:slug)
  const isOrgPage = firstSegment === 'org';
  const orgSlug = isOrgPage ? secondSegment : undefined;

  // 2. Check if it's a Location page (prefix /:slug, where slug is not a system prefix)
  const isLocationPage = !isOrgPage && !!firstSegment && !SYSTEM_PATH_PREFIXES.has(firstSegment);
  const locationSlug = isLocationPage ? firstSegment : undefined;

  const isStickyPath = STICKY_ORG_PATHS.has(firstSegment);

  const effectiveIsOrgPage = isOrgPage || (isStickyPath && !!pinnedOrgSlug);
  const effectiveOrgSlug = orgSlug ?? (effectiveIsOrgPage ? pinnedOrgSlug : undefined);

  const effectiveIsLocationPage = isLocationPage || (isStickyPath && !!pinnedLocationSlug);
  const effectiveLocationSlug = locationSlug ?? (effectiveIsLocationPage ? pinnedLocationSlug : undefined);

  // Pin/unpin slugs as user navigates
  useEffect(() => {
    if (orgSlug && !isStickyPath) {
      setPinnedOrgSlug(orgSlug);
    } else if (!orgSlug && !isStickyPath) {
      setPinnedOrgSlug(undefined);
    }
  }, [orgSlug, isStickyPath]);

  useEffect(() => {
    if (locationSlug && !isStickyPath) {
      setPinnedLocationSlug(locationSlug);
    } else if (!locationSlug && !isStickyPath) {
      setPinnedLocationSlug(undefined);
    }
  }, [locationSlug, isStickyPath]);

  // Fetch locations list for the sidebar
  const { data: locationsRawData, isLoading: isLocationsLoading } = useQuery(
    getLocations,
    undefined,
    { enabled: isOpen }
  ) as { data: any; isLoading: boolean };
  const sidebarLocations: any[] = (locationsRawData as any) ?? [];
  const locationsWithSlug = sidebarLocations.filter((loc: any) => loc.slug && loc.locationCode !== 'CA');
  const hubSidebarLocations = sortHubLocations(
    locationsWithSlug.filter((loc: any) => isHubLocationSlug(loc.slug))
  );

  // Fetch org details if inside org context
  const { data: orgRawData, isLoading: isOrgLoading } = useQuery(
    getOrganizationBySlug,
    { slug: effectiveOrgSlug! },
    { enabled: !!effectiveOrgSlug }
  );
  const orgData = orgRawData as any;

  // Compute org hidden sections using two-layer inheritance:
  // org-level override takes precedence over type defaults
  const typeDefaultHiddenSections: string[] = (effectiveIsOrgPage && !isOrgLoading)
    ? ((orgData?.organizationType?.settings as any)?.hiddenSections ?? [])
    : [];
  const orgSettings = (effectiveIsOrgPage && !isOrgLoading) ? (orgData?.settings as any) : null;
  const orgHasOverride = orgSettings != null && 'hiddenSections' in orgSettings;
  const orgHiddenSections: string[] = (effectiveIsOrgPage && !isOrgLoading)
    ? (orgHasOverride ? (orgSettings.hiddenSections ?? []) : typeDefaultHiddenSections)
    : [];

  // Load section visibility settings
  useEffect(() => {
    const loadSectionVisibility = async () => {
      try {
        const sections = await getSectionVisibility();
        const visibilityMap: Record<string, { isVisible: boolean, isActive: boolean, customLabel?: string | null }> = {};
        sections.forEach((section: any) => {
          visibilityMap[section.sectionKey] = {
            isVisible: section.isVisible,
            isActive: section.isActive,
            customLabel: section.customLabel
          };
        });
        setVisibleSections(visibilityMap);
      } catch (error) {
        console.error('Failed to load section visibility:', error);
      } finally {
        setVisibilityLoaded(true);
      }
    };
    loadSectionVisibility();
  }, []);

  // Check if a section should be shown in navigation
  const isSectionVisible = (sectionKey?: string): boolean => {
    if (!sectionKey) return true;

    // Not on an org page and not on a location page: only show the Locations (previously "organizations") entry point
    if (!effectiveIsOrgPage && !effectiveIsLocationPage && sectionKey !== 'organizations') return false;

    // On org page or location page: hide the "organizations" (or "locations") list item
    if ((effectiveIsOrgPage || effectiveIsLocationPage) && sectionKey === 'organizations') return false;

    const section = visibleSections[sectionKey];
    if (!section) return true; // Default to visible if no setting
    if (!section.isActive) return false; // Not active: hidden for everyone
    if (!section.isVisible) return !!isAdministrator(user); // Active but not visible: admin-only
    return true;
  };

  // Get custom label for a section if one exists
  const getSectionLabel = (defaultLabel: string, sectionKey?: string): string => {
    if (!sectionKey) return defaultLabel;
    const section = visibleSections[sectionKey];
    return section?.customLabel || defaultLabel;
  };

  // Determine if we're on a location/dashboard page (not an individual org page)

  // Community section label — the section that should only appear in "community" nav mode
  const COMMUNITY_SECTION_LABEL = 'Community';

  // On mobile, only show the Information section (Notices, Events, Organizations)
  const MOBILE_ALLOWED_PATHS = ['/notices', '/events', '/organizations'];

  // Filter menu sections based on visibility and navMode (on location pages)
  const filteredMenuSections = config.menuSections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (!isSectionVisible(item.sectionKey)) return false;
      // On mobile, restrict certain paths UNLESS we are in an admin section
      if (isCompact && !MOBILE_ALLOWED_PATHS.includes(item.path) && !location.pathname.startsWith('/admin')) return false;
      return true;
    })
  })).filter(section => {
    if (section.items.length === 0) return false;

    // Custom filtering for Community UX
    const isCommunityRoute = location.pathname.startsWith('/p/');
    const isDashboard = location.pathname === '/dashboard';

    if (isCommunityRoute) {
      // Hide non-Community sections from Community pages to keep them focused
      if (section.label === 'Information' || section.label === 'Community') return false;
      // Community pages manage their own sidebar sections — skip navMode filtering
      return true;
    }

    return true;
  });

  const toggleSection = (sectionLabel: string, alwaysOpen?: boolean) => {
    if (alwaysOpen) return; // Sections marked alwaysOpen cannot be collapsed
    setExpandedSections(prev => ({
      ...prev,
      [sectionLabel]: !prev[sectionLabel]
    }));
  };

  // Helper function to get org-aware or location-aware path
  const getLocationAwarePath = (basePath: string) => {
    if (basePath.startsWith('/p/')) return basePath;

    // On org page: scope paths that have actual org-scoped routes under /org/:slug/...
    if (effectiveIsOrgPage && effectiveOrgSlug && basePath !== '/dashboard' && !basePath.startsWith('/admin')) {
      const sectionKey = basePath.replace(/^\//, '');
      if (ORG_SECTIONS.has(sectionKey)) {
        return `/org/${effectiveOrgSlug}${basePath}`;
      }
    }

    // On location page: scope paths under /:slug/...
    if (effectiveIsLocationPage && effectiveLocationSlug && basePath !== '/dashboard' && !basePath.startsWith('/admin')) {
      const sectionKey = basePath.replace(/^\//, '');
      if (ORG_SECTIONS.has(sectionKey)) {
        return `/${effectiveLocationSlug}${basePath}`;
      }
    }

    return basePath;
  };

  const renderMenuItem = (item: any, level = 0) => {
    const Icon = item.icon;
    const locationAwarePath = getLocationAwarePath(item.path);
    // Check if active by matching both the base path and location-aware path
    const isActive = location.pathname === item.path ||
      location.pathname === locationAwarePath ||
      location.pathname.endsWith(item.path);
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <div key={item.path} className="mb-1">
          <button
            onClick={() => toggleSection(item.label)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sidebar-text-muted hover:bg-sidebar-hover transition-colors group"
          >
            {expandedSections[item.label] ? (
              <Circle className="w-4 h-4 text-sidebar-text-muted" />
            ) : (
              <Circle className="w-4 h-4 text-sidebar-text-muted" />
            )}
            <Icon className={cn('w-5 h-5', item.color || 'text-sidebar-text-muted')} />
            <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
            {expandedSections[item.label] ? (
              <ChevronDown className="w-4 h-4 text-sidebar-text-muted" />
            ) : (
              <ChevronRight className="w-4 h-4 text-sidebar-text-muted" />
            )}
          </button>

          {expandedSections[item.label] && (
            <div className="mt-1 ml-6">
              {item.children.map((child: any) => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={locationAwarePath}
        onClick={onNavigate}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 transition-colors group',
          isActive
            ? 'bg-sidebar-active text-sidebar-text'
            : 'text-sidebar-text hover:bg-sidebar-hover'
        )}
      >
        <Icon className={cn('w-5 h-5', item.color || 'text-sidebar-text-muted')} />
        <span className="flex-1 text-left text-base font-medium">
          {getSectionLabel(item.label, item.sectionKey)}
        </span>
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-full z-40 transition-all duration-300 bg-sidebar-bg border-r border-sidebar-border/100 flex flex-col overflow-hidden',
        isOpen ? 'w-64 lg:w-[var(--sidebar-width)]' : 'w-0',
        'lg:translate-x-0',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      {/* Brand Header */}
      <div className="border-b border-sidebar-border px-3 py-2 flex items-center justify-between" style={{ minHeight: '4rem' }}>
        <button onClick={goHome} className="flex-1 min-w-0 text-left flex items-center gap-2">
          <span className="text-xl font-bold text-sidebar-foreground">Invest Ottawa</span>
        </button>
        <button
          onClick={onToggle}
          className="p-2 hover:bg-sidebar-hover rounded-lg transition-colors flex-shrink-0"
          title="Collapse sidebar"
        >
          <ChevronRight className="w-4 h-4 text-sidebar-text-muted" />
        </button>
      </div>


      {/* Custom Sidebar Content */}
      {config.customSidebarContent && (
        <div className="border-b border-sidebar-border">
          {config.customSidebarContent}
        </div>
      )}


      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto">
        {!visibilityLoaded ? (
          <div className="px-4 py-6 text-center">
            <div className="animate-pulse space-y-3">
              <div className="h-3 bg-sidebar-hover rounded w-20"></div>
              <div className="h-8 bg-sidebar-hover rounded"></div>
              <div className="h-8 bg-sidebar-hover rounded"></div>
              <div className="h-3 bg-sidebar-hover rounded w-16 mt-4"></div>
              <div className="h-8 bg-sidebar-hover rounded"></div>
              <div className="h-8 bg-sidebar-hover rounded"></div>
              <div className="h-8 bg-sidebar-hover rounded"></div>
            </div>
          </div>
        ) : (
          <>
            {!isHubLanding && filteredMenuSections.map((section, index) => (
              <div key={section.label} className={index === 0 ? "-mt-px" : index === filteredMenuSections.length - 1 ? "" : "mb-0"}>
                {/* Section Header - Only show if label is not empty */}
                {section.label && (
                  section.alwaysOpen ? (
                    <div className="w-full flex items-center gap-2 px-4 py-2 text-sidebar-text-muted border-t sidebar-section-header border-sidebar-border/20">
                      <span className="flex-1 text-left text-base font-medium">{section.label}</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => toggleSection(section.label)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sidebar-text-muted hover:bg-sidebar-hover transition-colors group border-t sidebar-section-header border-sidebar-border/20"
                    >
                      <span className="flex-1 text-left text-base font-medium">{section.label}</span>
                    </button>
                  )
                )}

                {(!section.label || section.alwaysOpen || expandedSections[section.label]) && (
                  <div className={section.label ? "mt-1" : ""}>
                    {section.items.map((item) => renderMenuItem(item))}
                  </div>
                )}
              </div>
            ))}

            {/* Hub landing — only the three Invest Ottawa locations */}
            {isHubLanding && (
              <div>
                {isLocationsLoading && (
                  <div className="px-4 py-2 space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-7 bg-sidebar-hover rounded animate-pulse" />
                    ))}
                  </div>
                )}
                {!isLocationsLoading && hubSidebarLocations.map((loc: any) => (
                  <Link
                    key={loc.id}
                    to={`/${loc.slug}/information-desk`}
                    onClick={onNavigate}
                    className="w-full flex items-center gap-3 px-4 py-3 transition-colors text-sidebar-text hover:bg-sidebar-hover"
                  >
                    <span className="flex-1 text-left text-base font-medium truncate">
                      {loc.slug === 'killaloe' ? 'KO' : loc.name}
                    </span>
                  </Link>
                ))}
              </div>
            )}

            {/* Locations — flat list when not inside a location (non-hub landing) */}
            {!isHubLanding && !effectiveIsLocationPage && !effectiveIsOrgPage && !location.pathname.startsWith('/admin') && (
              <div>
                {isLocationsLoading && (
                  <div className="px-4 py-2 space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-7 bg-sidebar-hover rounded animate-pulse" />
                    ))}
                  </div>
                )}
                {!isLocationsLoading && locationsWithSlug.map((loc: any) => (
                  <Link
                    key={loc.id}
                    to={`/${loc.slug}/information-desk`}
                    onClick={onNavigate}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 transition-colors',
                      location.pathname.startsWith(`/${loc.slug}`)
                        ? 'bg-sidebar-active text-sidebar-text'
                        : 'text-sidebar-text hover:bg-sidebar-hover'
                    )}
                  >
                    <span className="flex-1 text-left text-sm font-medium truncate">
                      {loc.name}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </nav>

      {/* Admin back-link — shown on /admin pages */}
      {location.pathname.startsWith('/admin') && (
        <div className="border-t border-sidebar-border p-2">
          <Link
            to="/dashboard"
            onClick={onNavigate}
            className="w-full flex items-center gap-3 px-4 py-2 text-sidebar-text hover:bg-sidebar-hover rounded-lg transition-colors text-sm"
          >
            <span>Dashboard</span>
          </Link>
        </div>
      )}

      {/* Bottom Actions */}
      {isAdministrator(user) && location.pathname.startsWith('/dashboard') && (
        <div className="border-t border-sidebar-border p-2">
          <Link
            to="/admin"
            onClick={onNavigate}
            className="w-full flex items-center gap-3 px-4 py-2 text-sidebar-text hover:bg-sidebar-hover rounded-lg transition-colors"
          >
            <span className="text-base">Admin</span>
          </Link>
        </div>
      )}
    </aside>
  );
}

// Memoize to prevent unnecessary re-renders when config hasn't changed
export default React.memo(CommonSidebar, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.config === nextProps.config &&
    isAdministrator(prevProps.user) === isAdministrator(nextProps.user)
  );
});



